import React from 'react';
import { daysSince, gmailLink, rowNote, type Conversation } from '../../utils/conversations';

/**
 * The rows on Dispatches, lifted out of pages/portal/Conversations.tsx on
 * 16 Oct 2026 — that file carries a long history in its header and had grown
 * past the size a single safe overwrite can carry.
 *
 * WHAT CHANGED WHEN THEY MOVED: **the age stopped shouting.**
 *
 * The day count was set in Jost at text-xl, which made it the largest thing
 * in every row — larger than who the mail was from and larger than what it
 * was about. A screen whose job is "who owes whom a reply" was leading with
 * a number, and because every row wore the same number treatment, ten days
 * and two days looked alike anyway: the loudest element on the page was
 * also the least discriminating.
 *
 * So the order of loudness now matches the order of interest — the
 * correspondent, then the subject, then the age, which is metadata and is
 * set like metadata. Sorting still runs oldest-first, which is the part of
 * the screen that was always doing the real work of surfacing age.
 *
 * WHAT DELIBERATELY DID NOT CHANGE: the grey panel with the paper left
 * edge. It looks like a SaaS inbox card and it is not one — Tom picked it
 * out himself (*"we can put both in the grey style box — that looks
 * stylish"*) and asked for it on every row. Applied to everything it
 * signals nothing, which is the point: it is decoration, not a warning.
 * Leave it alone.
 */
function Row({
  row,
  busy,
  viewer,
  onDone,
  onReopen
}: {
  row: Conversation;
  busy: boolean;
  viewer: string;
  onDone: (row: Conversation) => void;
  onReopen: (row: Conversation) => void;
}) {
  const days = daysSince(row.lastAt);
  const link = gmailLink(row, viewer);
  const note = row.done ? '' : rowNote(row);
  const age = days === 0 ? 'Today' : `${days} ${days === 1 ? 'day' : 'days'}`;
  return <div className={`flex flex-col gap-4 border-b border-l-2 border-ruleDark/60 border-l-paper bg-paper/[0.04] py-5 pl-4 pr-3 last:border-b-ruleDark sm:flex-row sm:items-start sm:gap-10 sm:pl-5 ${row.done ? 'opacity-50' : ''}`} data-id="element-932" data-mp="153m2kddzmi5v">
      <div className="min-w-0 flex-1" data-id="element-933" data-mp="1ayiuqdeii2bw">
        <a href={link || undefined} target="_blank" rel="noopener noreferrer" className="inline-block font-display text-[1.25rem] leading-snug text-paper transition-opacity duration-150 ease-out hover:opacity-70" data-id="element-934" data-mp="15yxv00rippy4">
          {row.org}
          {row.person && <span className="text-paper/45" data-id="element-935" data-mp="ra1qgdoo6v29">{` — ${row.person}`}</span>}
          <span aria-hidden="true" className="ml-2 text-[0.7rem] text-paper/30" data-id="element-936" data-mp="r8qlwbnehja3">
            ↗
          </span>
        </a>

        <p className="m-0 mt-2 text-[1.0625rem] leading-snug text-paper/70" data-id="element-937" data-mp="n62abw7axy9f">{row.subject}</p>

        {note && <p className="label-sm m-0 mt-2.5 text-paper/45" data-id="element-938" data-mp="da92ur8fwuyi">{note}</p>}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-5 sm:w-[12rem] sm:justify-end" data-id="element-939" data-mp="dcd411eyffv3">
        <span className="label-sm whitespace-nowrap text-paper/40" data-id="element-940" data-mp="8d8vmczutm5t">{age}</span>

        <button type="button" onClick={() => row.done ? onReopen(row) : onDone(row)} disabled={busy} className="label inline-flex h-11 shrink-0 items-center border border-paper/30 px-4 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper disabled:opacity-40" data-id="element-941" data-mp="3uc3gn8xn5cc0">
          {row.done ? 'Reopen' : 'Done'}
        </button>
      </div>
    </div>;
}
export function DispatchGroup({
  title,
  rows,
  busyId,
  viewer,
  onDone,
  onReopen
}: {
  title: string;
  rows: Conversation[];
  busyId: string;
  viewer: string;
  onDone: (row: Conversation) => void;
  onReopen: (row: Conversation) => void;
}) {
  if (rows.length === 0) return null;
  return <section className="mt-12" data-id="element-942" data-mp="2svnmh73inlvz">
      <h2 className="label m-0 pb-3 text-paper" data-id="element-943" data-mp="2swfgweldndko">{title}</h2>

      <div className="border-t border-ruleDark" data-id="element-944" data-mp="2stt9ceyhky2g">
        {rows.map((row, index) => <div key={row.threadId} className="row-in" style={{
        ['--row' as string]: Math.min(index, 10)
      }} data-id="element-945" data-mp="2xsmdvhvspeqh" data-mpi={index}>
            <Row row={row} busy={busyId === row.threadId} viewer={viewer} onDone={onDone} onReopen={onReopen} data-id="element-946" data-mp="yggt94ch51nm" />
          </div>)}
      </div>
    </section>;
}